import { ApolloClient } from 'apollo-client';
import type { ApolloLink } from 'apollo-link';
import { from, split } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { BatchHttpLink } from 'apollo-link-batch-http';

import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { cfetch, NoNetworkError, causedByAbortError } from '@confluence/network';

import { NetworkStatus, networkStatusLink, writeNetworkStatus } from './links/NetworkStatusLink.ts';
import { externalShareLink } from './links/ExternalShareLink.ts';
import { addSuperAdminDirectiveLink } from './links/SuperAdminLink.ts';
import { queryOverridesLink } from './links/QueryOverridesLink.ts';
import { networkErrorRetryLink } from './links/NetworkErrorRetryLink.ts';
import { createOnErrorLink } from './links/OnErrorLink.ts';
import { statusCodeRetryLink } from './links/StatusCodeRetryLink.ts';
import { graphqlSyntheticErrorGenerator } from './links/DevOnlyGraphqlErrorGenerator.ts';
import { ExperimentalLink } from './links/ExperimentalLink/index.ts';
import { webSocketLink } from './links/WebSocketLink.ts';
import { SSRLink } from './links/SSRLink.ts';
import { SSRTestLink } from './links/SSRTestLink.ts';
import { requestIdLink } from './links/RequestIdLink.ts';
import { fragmentMatcher } from './fragmentMatcher.ts';
import { dataIdFromObject } from './dataIdFromObject.ts';
import { cacheRedirects } from './cacheRedirects.ts';
import { isExperimental, isWS } from './graphqlAssertions.ts';
import { ClientSchema } from './types/ClientSchema.graphqls.ts';
import { createWrappedFetch, AGG_SLOW_URI } from './createWrappedFetch.ts';
import { SSRDataLink } from './links/SSRServerLink.ts';
import { SSRClientLink } from './links/SSRClientLink.ts';
import { BroadcastConfigurableInMemoryCache } from './broadcastConfigurableInMemoryCache.ts';

// NOTE: URI should be cleaned up along with `cc-agg-migration-use-agg-endpoint`, once all skip queries are gone
const URI = '/cgraphql';
const MAX_CONCURRENCY_FOR_THROTTLED = 8;
const wrappedFetch = createWrappedFetch(false);
const SSR_NAMESPACE = 'cgraphql';
const DEFAULT_MAX_BATCH_SIZE = 4;
const DEFAULT_BATCH_INTERVAL = 0;

export function resetApolloStats() {
	window.__APOLLO_STATS__ = {
		// Used in node_modules/apollo-client/bundle.esm.js
		broadcast: {},
		diffStore: {},
	};
}

const createCache = (): BroadcastConfigurableInMemoryCache => {
	return new BroadcastConfigurableInMemoryCache({
		fragmentMatcher,
		dataIdFromObject,
		cacheRedirects,
	});
};

/**
 * Override fetch function used in terminating (http batch) link, to throttle network requests.
 * Concurrent network requests are limited based on MAX_CONCURRENCY_FOR_THROTTLED constant.
 *
 * Realistically, this will be used when there are a large volume of queries are triggered together, upon page load. Therefore,
 * resolving requests in approximate order is sufficient.
 */
const requestPool: Promise<Response>[] = [];
const throttledFetch = (uri: string, options: RequestInit) => {
	if (requestPool.length >= MAX_CONCURRENCY_FOR_THROTTLED) {
		// pool is full, so queue up next request behind the first request in the pool
		const queuedFetchFn = async () => {
			await requestPool.shift();
			return wrappedFetch(uri, options);
		};

		const queuedFetch: Promise<Response> = queuedFetchFn();
		requestPool.push(queuedFetch);
		return queuedFetch;
	}

	// pool has available slot - add new request to pool and begin request immediately
	const nextFetch = wrappedFetch(uri, options);
	requestPool.push(nextFetch);
	return nextFetch;
};

const createLink = ({
	experimentalSchemaLinkOverride,
	gatewayGraphqlSchemaLinkOverride,
}: {
	experimentalSchemaLinkOverride?: ApolloLink;
	gatewayGraphqlSchemaLinkOverride?: ApolloLink;
}) => {
	const httpLinkOptions = {
		uri: URI,
		credentials: 'same-origin',
		fetch: wrappedFetch,
	};

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const SSRLinks: ApolloLink[] = [];
		if (process.env.BIFROST_TEMPLATE_BUILD !== 'true') {
			SSRLinks.push(addSuperAdminDirectiveLink);
		}
		SSRLinks.push(SSRDataLink(SSR_NAMESPACE), SSRLink());

		if (process.env.CLOUD_ENV === 'staging' || process.env.CLOUD_ENV === 'branch') {
			SSRLinks.push(SSRTestLink());
		}
		return from(SSRLinks).concat(
			gatewayGraphqlSchemaLinkOverride ||
				split(
					({ getContext }) => getContext().single,
					new HttpLink(httpLinkOptions),
					new BatchHttpLink({
						...httpLinkOptions,
						batchMax: DEFAULT_MAX_BATCH_SIZE,
						batchInterval: DEFAULT_BATCH_INTERVAL,
					}),
				),
		);
	} else {
		const httpLink = split(
			({ getContext }) => {
				return (
					getContext().single ||
					// In integration tests we deliberately block some queries in order to test the loading state
					// Randomly batching other queries with them will make UI fail to load
					// @ts-ignore TODO FIXME
					window.Cypress ||
					window.__CRITERION__ ||
					// @ts-ignore Property 'isPlaywright' does not exist on type 'Window & typeof globalThis'
					Boolean(window.isPlaywright) ||
					// we don't want query batching for contract tests
					// @ts-expect-error Property isContract doesn't exist on window
					Boolean(window.isContract) ||
					!StatsigConfigurations.isKillSwitchOn('confluence_spa_query_batching')
				);
			},
			new HttpLink(httpLinkOptions),
			split(
				({ getContext }) => !!getContext().throttle,
				new HttpLink({ ...httpLinkOptions, fetch: throttledFetch }),
				new BatchHttpLink(httpLinkOptions),
			),
		);

		const processingLinks = [
			addSuperAdminDirectiveLink,
			SSRClientLink(SSR_NAMESPACE, apolloCache),
			...(expValEquals('cfe_use_persisted_queries_apollo', 'isEnabled', true)
				? [requestIdLink]
				: []),
			networkStatusLink(),
			statusCodeRetryLink,
			networkErrorRetryLink,
			externalShareLink,
		];

		let ccGraphLink: ApolloLink;
		if (process.env.NODE_ENV === 'testing') {
			// SSR and test env don't support websocket
			ccGraphLink = from(processingLinks).concat(httpLink);
		} else {
			ccGraphLink = from(processingLinks).split(isWS, webSocketLink(), httpLink);
		}

		const links = [createOnErrorLink(), graphqlSyntheticErrorGenerator()];

		// prevents adding bundle size for prod customers
		if (
			process.env.CLOUD_ENV === 'hello' ||
			process.env.CLOUD_ENV === 'staging' ||
			process.env.CLOUD_ENV === 'branch' ||
			(process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'testing')
		) {
			links.unshift(queryOverridesLink());
		}

		return from(links).split(
			// Condition
			(operation) => isExperimental(operation),
			// If query contains @experimental directive
			experimentalSchemaLinkOverride || ExperimentalLink(),
			// Else
			gatewayGraphqlSchemaLinkOverride || ccGraphLink,
		);
	}
};

export const apolloCache = createCache();

export const createClient = (
	options: {
		experimentalSchemaLinkOverride?: ApolloLink;
		gatewayGraphqlSchemaLinkOverride?: ApolloLink;
		initializeNewCache?: boolean;
	} = {},
) => {
	// Creates window.__APOLLO_STATS__ call before creating the client
	resetApolloStats();
	const cache = options.initializeNewCache ? createCache() : apolloCache;
	if (typeof window['__APOLLO_STATE__'] === 'object') {
		cache.restore(window['__APOLLO_STATE__']);
	}
	const client = new ApolloClient({
		// Prevent cache and network query to refresh when they are already preloaded on server-side.
		// Refreshing data will cause component to fallback to loading state.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		ssrMode: Boolean(process.env.REACT_SSR),
		link: createLink(options),
		typeDefs: ClientSchema,
		cache,
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		// Don't initialize the client cache for tests so we can mock it
		if (!options.gatewayGraphqlSchemaLinkOverride) {
			writeNetworkStatus(client.cache, NetworkStatus.ONLINE);
		}

		cfetch.subscribe((response, error) => {
			if (response) {
				// It's NetworkStatusLink's responsibility/concern to interpret
				// cc-graphql/agg errors (which may be carried in an OK HTTP response).
				if (response.url?.indexOf(URI) === -1 || response.url?.indexOf(AGG_SLOW_URI) === -1) {
					writeNetworkStatus(client.cache, NetworkStatus.ONLINE);
				}
			} else if (error instanceof NoNetworkError && !error.ignore && !causedByAbortError(error)) {
				writeNetworkStatus(client.cache, NetworkStatus.OFFLINE);
			}
		});
	}

	return client;
};
